import React, { useState, useRef, useEffect } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import { Icon, Loader } from "semantic-ui-react";
import { useVirtualizer } from "@tanstack/react-virtual";
import debounce from "lodash/debounce";
import Input from "../../Forms/Controls/Input/DefaultInput";
import { snakeToTitleCase } from "../../../utils";

function getSortTooltipLabel(label, sort) {
  if (sort !== "asc" && sort !== "desc") {
    return `Sort descending by ${label}`;
  }

  const currentOrder = sort === "desc" ? "descending" : "ascending";
  return `Sorted ${currentOrder} by ${label}`;
}

function resolveSortLabel(header) {
  const metaSortLabel = header.column.columnDef?.meta?.sortLabel;
  if (typeof metaSortLabel === "string" && metaSortLabel.trim()) {
    return metaSortLabel;
  }

  const headerContent =
    typeof header.column.columnDef.header === "function"
      ? header.column.columnDef.header(header.getContext())
      : header.column.columnDef.header;

  if (typeof headerContent === "string" && headerContent.trim()) {
    return headerContent.replace(/\*/g, "").trim();
  }

  return snakeToTitleCase(header.id);
}

function resolveTooltipSortState({
  hasServerSidePagination,
  headerId,
  sessionParams,
  sorting,
}) {
  if (hasServerSidePagination) {
    return sessionParams.sortColumn === headerId ? sessionParams.sortDirection : null;
  }

  const activeSort = sorting[0];
  if (!activeSort || activeSort.id !== headerId) {
    return null;
  }

  return activeSort.desc ? "desc" : "asc";
}

function createSortTooltipHandlers(setTooltip) {
  function showTooltip(event, meta) {
    if (!meta) return;
    const targetRect = event.currentTarget.getBoundingClientRect();

    setTooltip({
      ...meta,
      left: targetRect.left,
      top: targetRect.top - 12,
    });
  }

  function hideTooltip() {
    setTooltip(null);
  }

  return { hideTooltip, showTooltip };
}

export default function SmartTable({
  columns,
  data,
  disableSort = false,
  disableScrollToTop = false,
  getSortedState,
  hasServerSidePagination = false,
  hasRowPagination = true,
  serverSidePagination = {},
  loading = { silent: false, loading: false },
  loadingText = "Loading...",
  cssStyles = {},
}) {
  // Introducing Server-side pagination and sorting as an option.
  // See changes to sorting function in column headers and pagination footer.

  const [sorting, setSorting] = useState([]);
  const [isTop, setIsTop] = useState(true);
  const [scrollY, setScrollY] = useState(0);
  const [sortTooltip, setSortTooltip] = useState(null);

  const { serverSort, sessionParams } = serverSidePagination || {};
  const { hideTooltip, showTooltip } = createSortTooltipHandlers(setSortTooltip);

  let table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    manualSorting: hasServerSidePagination,
    manualPagination: hasServerSidePagination,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    defaultColumn: {
      width: "auto",
    },
  });

  const tableContainerRef = useRef(null);
  const firstRowRef = useRef(null);
  const rangeRef = useRef(null);

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: rows.length,
    estimateSize: () => 50,
    overscan: 50,
  });

  const totalSize = rowVirtualizer.getTotalSize();
  const items = rowVirtualizer.getVirtualItems();

  const paddingTop = items.length > 0 ? items?.[0]?.start || 0 : 0;
  const paddingBottom =
    items.length > 0 ? totalSize - (items?.[items.length - 1]?.end || 0) : 0;

  const scrollToIndex = (index, ...args) => {
    rowVirtualizer.scrollToIndex(index, ...args);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const { start, end } = rangeRef.current;

        const isVisible = index >= start && index <= end;

        if (!isVisible) {
          scrollToIndex(index, ...args);
        }
      });
    });
  };

  const handleScroll = debounce(() => {
    if (!tableContainerRef.current) return;
    let { scrollTop } = tableContainerRef.current;

    setScrollY(scrollTop);
  }, 100);

  useEffect(() => {
    scrollY === 0 ? setIsTop(true) : setIsTop(false);
  }, [scrollY]);

  useEffect(() => {
    let tableContainer;
    if (tableContainerRef.current) {
      tableContainer = tableContainerRef.current;
      tableContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (tableContainer)
        tableContainer.removeEventListener("scroll", handleScroll);
    };
  }, [tableContainerRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getSortedState) getSortedState(rows.map((obj) => obj.original));
  }, [sorting, data]);

  return (
    <>
      <div
        className="list-table"
        ref={tableContainerRef}
        style={cssStyles ? { ...cssStyles } : {}}
      >
        <div className="media-table__spinner-wrapper">
          {loading.silent && loading.loading && (
            <div className="filter-spinner-container">
              <span className="filter-spinner">
                <Loader inline active size="mini" inverted />
                {loadingText}
              </span>
            </div>
          )}
        </div>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <tr key={index}>
                {!hasServerSidePagination && hasRowPagination && <th />}
                {headerGroup.headers.map((header) => {
                  let sort = hasServerSidePagination
                    ? sessionParams.sortColumn === header.id
                      ? sessionParams.sortDirection
                      : null
                    : header.column.getIsSorted(); // TODO change this to the proper ssr state
                  const canShowSortTooltip =
                    !disableSort &&
                    header.column.getCanSort() &&
                    !header.column.columnDef?.meta?.disableSort;
                  const sortButton = (
                    <button
                      {...(hasServerSidePagination
                        ? {
                            onClick: () => {
                              if (header.column.getCanSort())
                                serverSort(header.id);
                            },
                          }
                        : {
                            onClick: !disableSort
                              ? header.column.getToggleSortingHandler()
                              : undefined,
                          })}
                      style={{
                        width: "100%",
                        cursor:
                          header.column.getCanSort() && !disableSort
                            ? "pointer"
                            : "default",
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                      {!disableSort &&
                        header.column.getCanSort() &&
                        !header.column.columnDef?.meta?.disableSort && (
                          <SortIndicator sort={sort} />
                        )}

                      {disableSort ||
                        (header.column.columnDef?.meta?.disableSort && (
                          <span style={{ opacity: 0 }}>
                            <SortIndicator sort={sort} />
                          </span>
                        ))}
                    </button>
                  );

                  return (
                    <th
                      key={header.id}
                      style={{
                        ...header.column.columnDef?.meta?.style,
                        width: header.column.columnDef?.meta?.width ?? "auto",
                      }}
                      className={header.column.columnDef?.meta?.cellClassName}
                    >
                      {canShowSortTooltip ? (
                        <span
                          onMouseEnter={(event) =>
                            showTooltip(event, {
                              label: resolveSortLabel(header),
                              key: header.id,
                            })
                          }
                          onMouseLeave={hideTooltip}
                          onBlur={hideTooltip}
                        >
                          {sortButton}
                        </span>
                      ) : (
                        sortButton
                      )}
                    </th>
                  );
                })}
                {!disableScrollToTop && !isTop && (
                  <th className="table-scroll-top">
                    <button onClick={() => scrollToIndex(0)}>
                      <Icon name="arrow up" />
                    </button>
                  </th>
                )}
              </tr>
            ))}
          </thead>
          <tbody>
            <tr
              ref={firstRowRef}
              style={{ height: 0, border: "none", padding: 0 }}
            >
              <td style={{ display: "none" }}></td>
            </tr>
            {paddingTop > 0 && (
              <tr>
                <td style={{ height: `${paddingTop}px` }} />
              </tr>
            )}
            {items.map((virtualRow) => {
              const row = rows[virtualRow.index];

              return (
                <tr key={virtualRow.key} ref={virtualRow.measureRef}>
                  {!hasServerSidePagination && hasRowPagination && (
                    <td>{virtualRow.index + 1}</td>
                  )}
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef?.meta?.cellClassName}
                      style={cell.column.columnDef?.meta?.style ?? undefined}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}

            {paddingBottom > 0 && (
              <tr>
                <td style={{ height: `${paddingBottom}px` }} />
              </tr>
            )}
          </tbody>
        </table>
        {sortTooltip ? (
          <div
            className="report-table__sort-control-tooltip"
            style={{
              left: `${sortTooltip.left}px`,
              top: `${sortTooltip.top}px`,
            }}
          >
            {getSortTooltipLabel(
              sortTooltip.label,
              resolveTooltipSortState({
                hasServerSidePagination,
                headerId: sortTooltip.key,
                sessionParams,
                sorting,
              })
            )}
          </div>
        ) : null}
      </div>
      <footer>
        {hasServerSidePagination && (
          <Pagination serverSidePagination={serverSidePagination} />
        )}
      </footer>
    </>
  );
}

function SortIndicator({ sort }) {
  return (
    <span className="sort-indicator">
      <span className="sort-indicator-container">
        <span className={`up ${sort === "asc" ? "active" : "inactive"}`} />
        <span className={`down ${sort === "desc" ? "active" : "inactive"}`} />
      </span>
    </span>
  );
}

function Pagination({ serverSidePagination }) {
  const {
    pageNext,
    pagePrev,
    pageFirst,
    pageLast,
    sessionMetadata,
    sessionParams,
    selectPage,
  } = serverSidePagination || {};

  const pageNavigateDisabled = sessionMetadata.total_pages === 1;

  return (
    <>
      <div className="table-default__pagination">
        {/* <div className="metadata">
          <select
          value={table.getState().pagination.pageSize}
          onChange={(e) => {
            table.setPageSize(Number(e.target.value));
          }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div> */}

        {/* <div className="metadata">
          <span>Go to page</span>
          <select
            value={sessionParams.pageSize}
            onChange={(e) => {
              console.log(e);
              // table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 20, 30, 40, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div> */}
        <div className="arrow__wrapper">
          <button
            className="arrow"
            onClick={() => {
              if (pageNavigateDisabled) return;
              pageFirst();
            }}
          >
            <Icon name="angle double left" />
          </button>
          <button
            className="arrow"
            // onClick={() => table.previousPage()}
            disabled={pageNavigateDisabled}
            style={{
              opacity: pageNavigateDisabled ? 0.5 : 1,
            }}
            onClick={() => {
              if (pageNavigateDisabled) return;
              pagePrev();
            }}
            // disabled={!table.getCanPreviousPage()}
          >
            <Icon name="angle left" />
          </button>
          <span className="page-number">
            <div>Page</div>
            <Input
              value={sessionParams.page}
              label={""}
              name={"number"}
              isDisabled={false}
              inputStyle={{
                width: "70px",
                fontWeight: "700",
              }}
              onChange={selectPage}
              type="number"
              min={1}
              max={sessionMetadata.total_pages}
            />
            of {sessionMetadata.total_pages}
          </span>
          <button
            className="arrow"
            disabled={pageNavigateDisabled}
            style={{
              opacity: pageNavigateDisabled ? 0.5 : 1,
            }}
            onClick={() => {
              if (pageNavigateDisabled) return;
              pageNext();
            }}
          >
            <Icon name="angle right" />
          </button>
          <button
            className="arrow"
            onClick={() => {
              if (pageNavigateDisabled) return;
              pageLast();
            }}
          >
            <Icon name="angle double right" />
          </button>
        </div>
      </div>
    </>
  );
}
